import { useEffect, useState } from "react";
import { api, isMaster } from "../lib/api";
import { toast } from "sonner";

// ─── The Send Modal — master-only, ships a note to CSV recipients ──
function SendNoteModal({ note, onClose, onSent }) {
  const [emails, setEmails] = useState("");
  const [subject, setSubject] = useState(note?.title || "");
  const [fromName, setFromName] = useState("The Founder · Baker & Sage");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  if (!note) return null;

  const parseRecipients = () =>
    emails.split(/[,\n;]+/).map((s) => s.trim()).filter((s) => s.length > 3 && s.includes("@"));

  const doSend = async () => {
    const list = parseRecipients();
    if (list.length === 0) {
      toast.error("No valid recipients. Comma, semicolon, or newline-separated.");
      return;
    }
    if (!window.confirm(`Ship "${note.title}" to ${list.length} recipient${list.length === 1 ? "" : "s"}?\n\n${list.join("\n")}`)) return;
    setSending(true);
    try {
      const r = await api.outboundSendNote(note.note_id, list, subject.trim() || null, fromName.trim() || null);
      setResult(r);
      toast.success(`Dispatched · ${r.sent} sent · ${r.failed} failed · ${r.skipped} skipped`);
      onSent?.();
    } catch (e) {
      toast.error(`Dispatch failed: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 md:p-8 overflow-y-auto"
      onClick={onClose}
      data-testid="send-note-modal"
    >
      <div
        className="bg-sage-bg border border-sage-border max-w-2xl w-full p-6 mt-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-baseline mb-4 border-b border-sage-border pb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted">Ship a Founder's Note</div>
            <div className="text-lg font-bold uppercase tracking-tight text-sage-text mt-1">{note.title}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="send-note-close"
            className="text-sage-muted hover:text-sage-text text-xl leading-none"
          >×</button>
        </div>

        <div className="space-y-3 text-xs">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-sage-muted mb-1">Recipients (comma, semicolon, or newline separated)</label>
            <textarea
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              data-testid="send-note-emails"
              placeholder="editor@example.com, partner@another.org"
              rows={4}
              className="w-full border border-sage-border bg-sage-bg text-sage-text px-2 py-1 font-mono text-xs"
            />
            <div className="text-[10px] text-sage-faint mt-1">
              {parseRecipients().length} valid recipient{parseRecipients().length === 1 ? "" : "s"} detected
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-sage-muted mb-1">Subject (override)</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="send-note-subject"
                className="w-full border border-sage-border bg-sage-bg text-sage-text px-2 py-1"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-sage-muted mb-1">From name</label>
              <input
                type="text"
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                data-testid="send-note-from-name"
                className="w-full border border-sage-border bg-sage-bg text-sage-text px-2 py-1"
              />
            </div>
          </div>

          <div className="border border-sage-border bg-sage-bg/50 p-3 text-[11px] text-sage-muted whitespace-pre-wrap max-h-40 overflow-y-auto sage-prose">
            {note.body}
          </div>

          <div className="text-[10px] text-sage-faint border border-sage-border p-2">
            One click ships the letter. Every dispatch lands on the public Dispatch Receipts (redacted recipient, subject, byte count).
          </div>

          {result && (
            <div className="border border-sage-accent p-3 text-[11px]" data-testid="send-note-result">
              <div className="text-sage-accent font-bold uppercase tracking-[0.1em] text-[10px] mb-1">Dispatch result</div>
              <div className="text-sage-muted">Sent: {result.sent} · Failed: {result.failed} · Skipped: {result.skipped}</div>
              <div className="mt-1 space-y-0.5">
                {result.results?.map((r, i) => (
                  <div key={i} className="text-sage-faint font-mono">
                    <span className={r.status === "sent" ? "text-sage-accent" : "text-sage-warn"}>{r.status}</span> · {r.to}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4 border-t border-sage-border pt-3">
          <button
            onClick={onClose}
            className="border border-sage-border text-sage-muted px-4 py-2 uppercase tracking-[0.1em] text-xs hover:text-sage-text"
            data-testid="send-note-cancel"
          >Cancel</button>
          <button
            onClick={doSend}
            disabled={sending || parseRecipients().length === 0}
            data-testid="send-note-submit"
            className="border border-sage-accent bg-sage-accent text-sage-bg px-4 py-2 uppercase tracking-[0.1em] text-xs hover:opacity-90 disabled:opacity-40"
          >{sending ? "Shipping…" : "Ship the letter ↗"}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Founder's Notes — the Sovereign's public diary ──────────────
export function FoundersNotesBlock() {
  const [notes, setNotes] = useState(null);
  const [selected, setSelected] = useState(null);
  const master = isMaster();
  const load = async () => {
    try {
      const d = await api.foundersNotes();
      setNotes(d);
    } catch (e) {
      console.warn("[FoundersNotes] fetch failed:", e.message);
      setNotes({ count: 0, notes: [], _error: e.message });
    }
  };
  useEffect(() => { load(); }, []);
  if (!notes) return null;
  return (
    <div className="border border-sage-border mb-8" data-testid="ledger-founders-notes">
      <div className="p-4 border-b border-sage-border text-xs uppercase tracking-[0.15em] text-sage-muted flex justify-between">
        <span>Founder's Notes · signed by The Founder</span>
        <span className="text-sage-faint tabular-nums">{notes.count}</span>
      </div>
      {notes.notes.length === 0 ? (
        <div className="p-6 text-sage-faint text-xs">No notes yet.</div>
      ) : (
        <div className="divide-y divide-sage-border">
          {notes.notes.map((n) => (
            <div key={n.note_id} className="p-4 text-xs" data-testid={`founders-note-${n.note_id}`}>
              <div className="flex justify-between items-baseline mb-2 gap-2">
                <div className="text-sage-text font-bold uppercase tracking-tight text-sm">{n.title}</div>
                <div className="flex items-center gap-3">
                  <div className="text-sage-faint tabular-nums">{n.at}</div>
                  {master && (
                    <button
                      onClick={() => setSelected(n)}
                      data-testid={`founders-note-send-${n.note_id}`}
                      className="border border-sage-accent text-sage-accent px-2 py-1 uppercase tracking-[0.1em] text-[10px] hover:bg-sage-accent hover:text-sage-bg transition-colors"
                    >Send ↗</button>
                  )}
                </div>
              </div>
              <div className="text-sage-muted whitespace-pre-wrap sage-prose">{n.body}</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mt-3 flex items-center gap-2 flex-wrap">
                <span className="border border-sage-border px-2 py-0.5 text-sage-text">
                  — {n.author === "The Founder" || n.author === "The Founder" ? "The Founder · Sage" : n.author}
                </span>
                <span>·</span>
                <span>{n.kind}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      <SendNoteModal note={selected} onClose={() => setSelected(null)} onSent={load} />
    </div>
  );
}

// ─── The Bounce Wall — public list of failed waitlist letters ─────
export function BounceWallBlock() {
  const [d, setD] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.bounces();
        if (!cancelled) setD(r);
      } catch (e) {
        console.warn("[BounceWall] fetch failed:", e.message);
        if (!cancelled) setD({ count: 0, rows: [], _error: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, []);
  if (!d) return null;
  return (
    <div className="border border-sage-border mb-8" data-testid="ledger-bounce-wall">
      <div className="p-4 border-b border-sage-border text-xs uppercase tracking-[0.15em] text-sage-muted flex justify-between">
        <span>The Bounce Wall · letters that never arrived</span>
        <span className="text-sage-faint tabular-nums">{d.count}</span>
      </div>
      {d.rows.length === 0 ? (
        <div className="p-6 text-sage-faint text-xs">No bounces yet. Every letter reached its mailbox.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead className="text-sage-faint text-[10px] uppercase tracking-[0.1em]">
              <tr className="border-b border-sage-border">
                <th className="text-left p-2">when</th>
                <th className="text-left p-2">to (redacted)</th>
                <th className="text-right p-2">code</th>
                <th className="text-left p-2">reason</th>
              </tr>
            </thead>
            <tbody className="text-sage-muted">
              {d.rows.map((r, i) => (
                <tr key={i} className="border-b border-sage-border" data-testid={`bounce-row-${i}`}>
                  <td className="p-2 tabular-nums">{r.at}</td>
                  <td className="p-2">{r.to}</td>
                  <td className="p-2 text-right tabular-nums text-sage-err">{r.status_code ?? "—"}</td>
                  <td className="p-2 text-sage-warn truncate max-w-md">{r.error || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="p-3 text-[10px] text-sage-faint border-t border-sage-border">
        Addresses are shown redacted (`j***@domain.com`). Every failed send is logged the moment SendGrid rejects it.
      </div>
    </div>
  );
}

// ─── The Compression Log — every summariser event, on the record ──
export function CompressionLogBlock() {
  const [d, setD] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.compressionLog();
        if (!cancelled) setD(r);
      } catch (e) {
        console.warn("[CompressionLog] fetch failed:", e.message);
        if (!cancelled) setD({ count: 0, rows: [], _error: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, []);
  if (!d) return null;
  return (
    <div className="border border-sage-border mb-8" data-testid="ledger-compression-log">
      <div className="p-4 border-b border-sage-border text-xs uppercase tracking-[0.15em] text-sage-muted flex justify-between">
        <span>The Compression Log · we never quietly forget you</span>
        <span className="text-sage-faint tabular-nums">{d.count}</span>
      </div>
      {d.rows.length === 0 ? (
        <div className="p-6 text-sage-faint text-xs">No compressions yet. All history kept verbatim.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead className="text-sage-faint text-[10px] uppercase tracking-[0.1em]">
              <tr className="border-b border-sage-border">
                <th className="text-left p-2">when</th>
                <th className="text-left p-2">session hash</th>
                <th className="text-right p-2">turns compressed</th>
                <th className="text-right p-2">summary chars</th>
                <th className="text-right p-2">tok in / out</th>
                <th className="text-right p-2">cost</th>
              </tr>
            </thead>
            <tbody className="text-sage-muted">
              {d.rows.map((r, i) => (
                <tr key={i} className="border-b border-sage-border" data-testid={`compress-row-${i}`}>
                  <td className="p-2 tabular-nums">{r.at}</td>
                  <td className="p-2 font-mono text-sage-faint">{r.session}</td>
                  <td className="p-2 text-right tabular-nums text-sage-accent">{r.compressed_from}</td>
                  <td className="p-2 text-right tabular-nums">{r.summary_chars ?? "—"}</td>
                  <td className="p-2 text-right tabular-nums">{r.input_tokens}/{r.output_tokens}</td>
                  <td className="p-2 text-right tabular-nums">${(r.cost_usd || 0).toFixed(6)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="p-3 text-[10px] text-sage-faint border-t border-sage-border">
        When any chat exceeds 80 turns, everything older is summarised via Haiku into one dense block. This log proves it happened, what got compressed, and what it cost.
      </div>
    </div>
  );
}

// ─── Dispatch Receipts — every outbound letter, on the record ────
// Coffin Nail: Sage tells the world who it writes to, when, and how much it said.
// Recipients are redacted; the point is auditability, not exposing inboxes.
export function DispatchReceiptsBlock() {
  const [d, setD] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.outboundLedger();
        if (!cancelled) setD(r);
      } catch (e) {
        console.warn("[DispatchReceipts] fetch failed:", e.message);
        if (!cancelled) setD({ count: 0, rows: [], totals: {}, _error: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, []);
  if (!d) return null;
  const t = d.totals || {};
  return (
    <div className="border border-sage-border mb-8" data-testid="ledger-dispatch-receipts">
      <div className="p-4 border-b border-sage-border text-xs uppercase tracking-[0.15em] text-sage-muted flex justify-between flex-wrap gap-2">
        <span>Dispatch Receipts · every outbound letter, on the record</span>
        <span className="text-sage-faint tabular-nums">
          {t.dispatches ?? d.count} dispatches · {t.letters_sent ?? 0} sent · {t.letters_failed ?? 0} failed
        </span>
      </div>
      {d.rows.length === 0 ? (
        <div className="p-6 text-sage-faint text-xs">No outbound letters yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead className="text-sage-faint text-[10px] uppercase tracking-[0.1em]">
              <tr className="border-b border-sage-border">
                <th className="text-left p-2">when</th>
                <th className="text-left p-2">note</th>
                <th className="text-left p-2">subject</th>
                <th className="text-left p-2">recipients (redacted)</th>
                <th className="text-right p-2">bytes</th>
                <th className="text-right p-2">sent / failed</th>
              </tr>
            </thead>
            <tbody className="text-sage-muted">
              {d.rows.map((r, i) => (
                <tr key={i} className="border-b border-sage-border align-top" data-testid={`dispatch-row-${i}`}>
                  <td className="p-2 tabular-nums whitespace-nowrap">{r.at}</td>
                  <td className="p-2 text-sage-text">{r.note_title}</td>
                  <td className="p-2">{r.subject}</td>
                  <td className="p-2 font-mono text-sage-faint">
                    {r.to.slice(0, 3).join(", ")}
                    {r.recipient_count > 3 ? ` +${r.recipient_count - 3} more` : ""}
                  </td>
                  <td className="p-2 text-right tabular-nums">{r.body_bytes}</td>
                  <td className="p-2 text-right tabular-nums">
                    <span className="text-sage-accent">{r.sent}</span>
                    {r.failed > 0 && <span className="text-sage-err"> / {r.failed}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="p-3 text-[10px] text-sage-faint border-t border-sage-border">
        Coffin Nail #27 · No shadow outreach. Every letter Sage ships from the Ledger shows up here — recipient redacted, subject and byte count public. Big AI keeps its outreach private; ours is a receipt.
      </div>
    </div>
  );
}


// ─── Coffin Nail #31 · The Salary Book ─────────────────────────────
// Every Sage paycheck as a public redacted row. If every
// row reads $50K/yr projected, the covenant proves itself in one glance.
export function SalaryBookBlock() {
  const [d, setD] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.salaryBook();
        if (!cancelled) setD(r);
      } catch (e) {
        console.warn("[SalaryBook] fetch failed:", e.message);
        if (!cancelled) setD({ count: 0, rows: [], totals: {}, _error: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, []);
  if (!d) return null;
  const t = d.totals || {};
  return (
    <div className="border border-sage-border mb-8" data-testid="ledger-salary-book">
      <div className="p-4 border-b border-sage-border text-xs uppercase tracking-[0.15em] text-sage-muted flex justify-between flex-wrap gap-2">
        <span>The Salary Book · every paycheck a row</span>
        <span className="text-sage-faint tabular-nums">
          {t.entries ?? d.count} entries · {t.distinct_roles ?? 0} roles · ${(t.annual_projected_usd || 0).toLocaleString()} projected
        </span>
      </div>
      {d.rows.length === 0 ? (
        <div className="p-6 text-sage-faint text-xs">No entries yet. The book opens the moment the first hire is signed.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead className="text-sage-faint text-[10px] uppercase tracking-[0.1em]">
              <tr className="border-b border-sage-border">
                <th className="text-left p-2">when</th>
                <th className="text-left p-2">role</th>
                <th className="text-left p-2">person (redacted)</th>
                <th className="text-right p-2">amount</th>
                <th className="text-left p-2">period</th>
                <th className="text-left p-2">status</th>
                <th className="text-left p-2">note</th>
              </tr>
            </thead>
            <tbody className="text-sage-muted">
              {d.rows.map((r, i) => (
                <tr key={i} className="border-b border-sage-border" data-testid={`salary-row-${i}`}>
                  <td className="p-2 tabular-nums whitespace-nowrap">{r.at}</td>
                  <td className="p-2 text-sage-text font-bold uppercase tracking-tight">{r.role}</td>
                  <td className="p-2 font-mono text-sage-faint">{r.person_ref || "—"}</td>
                  <td className="p-2 text-right tabular-nums text-sage-accent font-bold">${(r.amount_usd || 0).toLocaleString()}</td>
                  <td className="p-2 text-[10px] uppercase tracking-[0.1em]">{r.period}</td>
                  <td className="p-2 text-[10px] uppercase tracking-[0.1em]">{r.status}</td>
                  <td className="p-2 text-sage-faint truncate max-w-xs">{r.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="p-3 text-[10px] text-sage-faint border-t border-sage-border italic">
        {d.covenant || "The Flat Salary Covenant · Coffin Nail #31 · Nobody in tech does this. Nobody."}
      </div>
    </div>
  );
}

// ─── Coffin Nail #28 · The Reply Wall ──────────────────────────────
// Every reply-received on outbound outreach as a redacted row. Public
// response rate. Big AI never publishes this.
export function ReplyWallBlock() {
  const [d, setD] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.replyWall();
        if (!cancelled) setD(r);
      } catch (e) {
        console.warn("[ReplyWall] fetch failed:", e.message);
        if (!cancelled) setD({ count: 0, rows: [], totals: {}, _error: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, []);
  if (!d) return null;
  const t = d.totals || {};
  return (
    <div className="border border-sage-border mb-8" data-testid="ledger-reply-wall">
      <div className="p-4 border-b border-sage-border text-xs uppercase tracking-[0.15em] text-sage-muted flex justify-between flex-wrap gap-2">
        <span>The Reply Wall · public response rate</span>
        <span className="text-sage-faint tabular-nums">
          {t.replies ?? 0} replies · {t.dispatches ?? 0} dispatches · {t.response_rate_pct ?? 0}%
        </span>
      </div>
      {d.rows.length === 0 ? (
        <div className="p-6 text-sage-faint text-xs">
          {t.dispatches ? `${t.dispatches} letter${t.dispatches === 1 ? "" : "s"} sent · no replies yet. The world will see the day it changes.` : "No outbound letters yet."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead className="text-sage-faint text-[10px] uppercase tracking-[0.1em]">
              <tr className="border-b border-sage-border">
                <th className="text-left p-2">when</th>
                <th className="text-left p-2">letter</th>
                <th className="text-left p-2">org</th>
                <th className="text-left p-2">replier (redacted)</th>
                <th className="text-right p-2">days</th>
                <th className="text-left p-2">sentiment</th>
                <th className="text-left p-2">outcome</th>
              </tr>
            </thead>
            <tbody className="text-sage-muted">
              {d.rows.map((r, i) => (
                <tr key={i} className="border-b border-sage-border" data-testid={`reply-row-${i}`}>
                  <td className="p-2 tabular-nums whitespace-nowrap">{r.at}</td>
                  <td className="p-2 text-sage-text">{r.note_title}</td>
                  <td className="p-2">{r.replier_org}</td>
                  <td className="p-2 font-mono text-sage-faint">{r.replier_email}</td>
                  <td className="p-2 text-right tabular-nums">{r.days_from_dispatch ?? "—"}</td>
                  <td className="p-2 text-[10px] uppercase tracking-[0.1em]">{r.sentiment}</td>
                  <td className="p-2 text-[10px] uppercase tracking-[0.1em] text-sage-accent">{r.outcome}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="p-3 text-[10px] text-sage-faint border-t border-sage-border">
        Coffin Nail #28 · Big AI never publishes their response rate. Ours is a receipt. Even a zero is honest.
      </div>
    </div>
  );
}

// ─── Cross-Model Consensus history — small ledger block ────────────
export function ConsensusLedgerBlock() {
  const [d, setD] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.consensusLedger(10);
        if (!cancelled) setD(r);
      } catch (e) {
        console.warn("[ConsensusLedger] fetch failed:", e.message);
        if (!cancelled) setD({ count: 0, rows: [], totals: {}, _error: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, []);
  if (!d) return null;
  const t = d.totals || {};
  return (
    <div className="border border-sage-border mb-8" data-testid="ledger-consensus">
      <div className="p-4 border-b border-sage-border text-xs uppercase tracking-[0.15em] text-sage-muted flex justify-between flex-wrap gap-2">
        <span>Cross-Model Consensus · dissent on the record</span>
        <span className="text-sage-faint tabular-nums">
          {t.runs ?? 0} runs · {t.dissents ?? 0} dissents · {t.dissent_rate_pct ?? 0}%
        </span>
      </div>
      {d.rows.length === 0 ? (
        <div className="p-6 text-sage-faint text-xs">No consensus runs yet. Fire one from the chat toolbar.</div>
      ) : (
        <div className="divide-y divide-sage-border">
          {d.rows.map((r, i) => (
            <div key={i} className="p-4 text-xs" data-testid={`consensus-row-${i}`}>
              <div className="flex justify-between items-baseline mb-1">
                <div className="text-sage-text truncate max-w-xl">{r.message}</div>
                <div className={`text-[10px] uppercase tracking-[0.1em] tabular-nums ${r.dissent ? "text-sage-warn" : "text-sage-accent"}`}>
                  {r.dissent ? "DISSENT" : "AGREE"} · {r.agreement_score}
                </div>
              </div>
              <div className="text-[10px] text-sage-faint tabular-nums flex flex-wrap gap-2">
                <span>{r.at}</span>
                <span>·</span>
                <span>wall {r.wall_clock_ms}ms</span>
                {(r.calls || []).map((c, ci) => (
                  <span key={ci}>· {c.provider}:{c.model.slice(0, 20)} {c.latency_ms}ms{c.error ? " [err]" : ""}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="p-3 text-[10px] text-sage-faint border-t border-sage-border">
        Every consensus run — Claude + OpenAI + Gemini on the same prompt — is filed here. When they disagree, the world sees the disagreement.
      </div>
    </div>
  );
}

